import { currentAnalysisSelections, analysisSelections, studySelections, selectedStudies } from './SearchQuery'
import { favoriteQuery, favorite } from './AvailableAnalyses'
import { studyStats, getCurrentBluntQuery, setScreening, studyCollection, allArticlesVar} from 'model/StudyCollection'
import { all, reactive, Variable } from 'alkali'
import { get, post, patch } from 'tools/request'
import { questionToText, getShortScreeningName } from 'analysis/Question'
import app from 'app/app'

const Screening = Variable.with({
	name: '',
	description: '',
	votes: [],
	screening_uuid: '',
})

export const screening = currentAnalysisSelections.screening = reactive(currentAnalysisSelections.id).to(id =>
	get('/portal/AnalysisSelections/' + id + '/screening').catch(console.error)).as(Screening)
export function updateScreeningName({ name, description }) {
	let question = questionToText(analysisSelections.question.valueOf())
	name = question + ' - ' + name
	return patch('/portal/screening/' + screening.screening_uuid, {
		data: {
			name,
			description,
		}
	})
}
screening.shortName = screening.to(async screening => {
	if (screening) {
		return getShortScreeningName(screening.name, await analysisSelections.question)
	}
}, (shortName) => {
	let question = questionToText(analysisSelections.question.valueOf())
	screening.name.put(question + ' - ' + shortName)
})
screening.subscribe(() => screening.then(setScreening))

screening.ownsScreening = () => screening.to(async screening => screening?.username.toLowerCase() == (await app.user).LegacyUsername.toLowerCase())

screening.flag = async (options, study?) => {
	if (study) {
		studySelections.property(study.id).set('included', true) // mark that we are using this study
	}
	if (!(await screening))
		await createScreening()
	if (!screening.valueOf())
		return
	if (!(await screening.ownsScreening())) {
		return alert('This screening does not belong to you, only ' + screening.valueOf()?.username + ' can edit this screening.')
	}
	await post('/portal/AnalysisSelections/' + currentAnalysisSelections.id, {
		data: Object.assign({
			method: 'flag',
			screeningId: analysisSelections.get('screeningId')
		}, options)
	})
	let votes = screening.votes.valueOf()
	votes = votes.filter(vote => !options.articleIds.includes(getArticleIdOfVote(vote))) // clear out existing votes for these articles
	for (let articleId of options.articleIds) {
		votes.push(Object.assign({
			'article/guid': articleId,
		}, options, {
			notes: options.notes.valueOf()
		}))
	}
	if (options.notes)
		options.notes?.put('') // clear the notes afterwards
	screening.votes.put(votes)
}
export const counts = screening.counts = all([studyStats, screening, studyCollection, allArticlesVar], (stats, screening, collection, allArticles)  => {
	let votes = screening?.votes || []
	let { digitizedCount, undigitizedCount, groupedCount } = stats || {}
	let voteByArticle = _.keyBy(votes, getArticleIdOfVote)
	let counts = _.countBy(votes, vote => vote.type.split(' ')[0])
	let total = digitizedCount + undigitizedCount - groupedCount

	return Object.assign(counts, {
		total,
		toScreen: total - (counts.Include || 0) - (counts.Wrong || 0),
		toExtract: screening ? (counts.Include || 0) + (counts.Uncertain || 0) : total,
		analyzable: (screening ? votes.filter(vote =>
			vote.type.startsWith('Include') &&
			collection.find(study => getArticleId(study) == getArticleIdOfVote(vote))?.hasAnalyzableData).length : studyStats.analyzableCount),
	})
})
export const getArticleId = (study) => study.articleId || ('docdata/' + study.id)
export const getArticleIdOfVote = vote => vote.source == 'docdata' ? 'docdata/' + vote.uri : vote['article/guid']

export const voteForArticle = article => {
	let articleId = getArticleId(article)
	return screening.votes.to(votes => votes.find(vote => getArticleIdOfVote(vote) == articleId))
}
let screeningConfirmation
export function registerScreeningConfirmation(confirm) {
	screeningConfirmation = confirm
}

export async function createScreening() {
	let confirmationDialog
	try {
		if (selectedStudies.valueOf()?.length > 0 || !app.user.Settings?.Tour?.Screening) {
			confirmationDialog = await screeningConfirmation()
			if (!confirmationDialog)
				return
		}
		let newScreening = await post('/portal/Screening/', {
			data: {
				name: questionToText(analysisSelections.question.valueOf()) + ' - Screening Project',
				description: '',
				query: {
					'query/blunt': (await getCurrentBluntQuery()).bluntQuery,
				}
			}
		})
		let screeningId = analysisSelections.set('screeningId', newScreening.screening_uuid)
		screening.is(newScreening)
		if (app.user.Settings?.IncludeSelectedStudiesInScreening) {
			let flagged = screening.flag({
				type: 'Include',
				notes: '',
				articleIds: selectedStudies.valueOf().flatMap(id => {
					let study = studyCollection.valueOf()?.find(study => study.id == id)
					if (study)
						return [getArticleId(study), ...(study.additionalStudies || []).map(getArticleId)]
					else if (id > 0)
						return 'docdata/' + id
					else
						return id
				})
			})
			for (let id of selectedStudies) {
				studySelections.property(id).set('included', true) 
			}
			await flagged
		}
		if (!favorite.valueOf() && app.user.Settings?.AutoFavoriteScreeningAnalysis)
			favoriteQuery()
	} catch(error) {
		if (error.status == 403)
			alert('You do not have access to DOC Search, which is necessary for screening. In order to start screening, please contact your Client Success representative to update your screening permissions.')
		else
			throw error
	} finally {
		confirmationDialog?.hide()
	}
}

export const hasScreeningAccess = reactive(app.user).to(user => !!user?.FeatureConfig?.Screening?.Enabled)

